import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import api from "api";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { IFCLoader } from "web-ifc-three/IFCLoader";
import {
  IFCWALL, IFCWALLSTANDARDCASE, IFCCURTAINWALL,
  IFCSLAB,
  IFCDOOR,
  IFCWINDOW,
  IFCROOF,
  IFCCOLUMN,
  IFCBEAM, IFCMEMBER, IFCPLATE,
  IFCSTAIR, IFCSTAIRFLIGHT,
  IFCFURNISHINGELEMENT,
  IFCRAILING,
  IFCLIGHTFIXTURE,
  IFCSPACE,
  IFCBUILDINGELEMENTPROXY,
  IFCCOVERING,
  IFCPILE,
  IFCFOOTING,
  IFCREINFORCINGBAR,
  IFCREINFORCINGMESH,
  IFCTENDON,
  IFCTENDONANCHOR,
  IFCRAMP, IFCRAMPFLIGHT,
  IFCELEMENTASSEMBLY,
  IFCDISCRETEACCESSORY,
} from "web-ifc";

import { Tooltip } from "@material-ui/core";
import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Straighten as StraightenIcon,
  SquareFoot as SquareFootIcon,
  GetApp as GetAppIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@material-ui/icons";
import LayersIcon from "@material-ui/icons/Layers";
import PlaceIcon from "@material-ui/icons/Place";
import StraightenIcon2 from "@material-ui/icons/Straighten";

import ScreenshotBim from "components/screenshot-bim/ScreenshotBim";

import "./BimViewer.css";

/** Remove grade e eixos auxiliares da cena. */
function disposeBimSceneHelpers(scene, helpersRef) {
  if (!scene || !helpersRef?.current) return;
  const h = helpersRef.current;
  ["grid", "axes"].forEach((key) => {
    const obj = h[key];
    if (!obj) return;
    try { scene.remove(obj); } catch {}
    try {
      obj.geometry?.dispose?.();
      const m = obj.material;
      if (Array.isArray(m)) m.forEach((x) => x.dispose?.());
      else m?.dispose?.();
    } catch {}
    h[key] = null;
  });
}

// ─── Categorias IFC para o painel de camadas ──────────────────────────────────
const IFC_CATEGORIES = [
  { name: "Paredes",      types: [IFCWALL, IFCWALLSTANDARDCASE, IFCCURTAINWALL] },
  { name: "Lajes",        types: [IFCSLAB] },
  { name: "Portas",       types: [IFCDOOR] },
  { name: "Janelas",      types: [IFCWINDOW] },
  { name: "Cobertura",    types: [IFCROOF] },
  { name: "Colunas",      types: [IFCCOLUMN] },
  { name: "Vigas",        types: [IFCBEAM, IFCMEMBER, IFCPLATE] },
  { name: "Escadas",      types: [IFCSTAIR, IFCSTAIRFLIGHT] },
  { name: "Gradis",       types: [IFCRAILING] },
  { name: "Iluminação",   types: [IFCLIGHTFIXTURE] },
  { name: "Mobiliário",   types: [IFCFURNISHINGELEMENT] },
  { name: "Espaços",      types: [IFCSPACE] },
];

// Tipos conhecidos mas menos comuns: usados na camada "Outros" para capturar
// qualquer elemento presente no IFC que não se enquadre nas categorias acima.
const IFC_CATCHALL_TYPES = [
  IFCBUILDINGELEMENTPROXY,
  IFCCOVERING,
  IFCPILE,
  IFCFOOTING,
  IFCREINFORCINGBAR,
  IFCREINFORCINGMESH,
  IFCTENDON,
  IFCTENDONANCHOR,
  IFCRAMP, IFCRAMPFLIGHT,
  IFCELEMENTASSEMBLY,
  IFCDISCRETEACCESSORY,
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatIfcValue(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === "object" && "value" in val) return val.value;
  if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") return val;
  return null;
}

function flattenIfcProps(props) {
  if (!props || typeof props !== "object") return {};
  const SKIP = new Set(["expressID", "type"]);
  const out = {};
  for (const [key, val] of Object.entries(props)) {
    if (SKIP.has(key)) continue;
    const formatted = formatIfcValue(val);
    if (formatted !== null && formatted !== undefined && formatted !== "") {
      out[key] = formatted;
    }
  }
  return out;
}

function safeJsonStringify(value) {
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
}

// ─── Componente ───────────────────────────────────────────────────────────────
const BimViewer = ({ token }) => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { project, date, folder_path: folderPathFromState } = location.state || {};

  const name = project?.name;
  const folderPath =
    folderPathFromState ||
    (name && date ? `projetos/${name}/${date}/bim/` : null);

  const [loading,    setLoading]    = useState(true);
  const [loadingMsg, setLoadingMsg] = useState("Carregando…");
  const [error,      setError]      = useState(null);

  const [assets,      setAssets]      = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Camadas derivadas do IFC (por categoria de tipo)
  const [layerToggles, setLayerToggles] = useState([]); // [{name, visible, subsetMesh}]
  const layerTogglesRef = useRef([]);
  useEffect(() => { layerTogglesRef.current = layerToggles; }, [layerToggles]);

  // Propriedades do elemento clicado
  const [selectedProps, setSelectedProps] = useState(null);

  // Painéis informativos (camadas + propriedades/medição)
  const [layersPanelOpen, setLayersPanelOpen] = useState(true);
  const [propsPanelOpen, setPropsPanelOpen] = useState(true);

  // Responsivo: em telas pequenas evitamos sobreposição automática
  // fechando o painel de propriedades; o usuário pode expandir manualmente.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqlSize = window.matchMedia("(max-width: 768px)");
    const mqlCoarse = window.matchMedia("(pointer: coarse)");

    const apply = () => {
      if (mqlSize.matches && mqlCoarse.matches) {
        setLayersPanelOpen(true);
        setPropsPanelOpen(false);
      } else {
        setLayersPanelOpen(true);
        setPropsPanelOpen(true);
      }
    };

    apply();
    const handler = () => apply();
    if (typeof mqlSize.addEventListener === "function" && typeof mqlCoarse.addEventListener === "function") {
      mqlSize.addEventListener("change", handler);
      mqlCoarse.addEventListener("change", handler);
      return () => {
        mqlSize.removeEventListener("change", handler);
        mqlCoarse.removeEventListener("change", handler);
      };
    }
    // Fallback Safari antigo
    if (typeof mqlSize.addListener === "function") mqlSize.addListener(handler);
    if (typeof mqlCoarse.addListener === "function") mqlCoarse.addListener(handler);
    return () => {
      if (typeof mqlSize.removeListener === "function") mqlSize.removeListener(handler);
      if (typeof mqlCoarse.removeListener === "function") mqlCoarse.removeListener(handler);
    };
  }, []);

  // Ferramentas de medição
  const [measureMode,       setMeasureMode]       = useState(null);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [measurementValue,  setMeasurementValue]  = useState(null);

  // Refs Three.js
  const canvasMountRef = useRef(null);
  const rendererRef    = useRef(null);
  const sceneRef       = useRef(null);
  const cameraRef      = useRef(null);
  const controlsRef    = useRef(null);

  // Refs IFC
  const ifcLoaderRef   = useRef(null);
  const ifcModelIdRef  = useRef(null);
  const modelRootRef   = useRef(null);

  // Raycasting
  const selectableMeshesRef = useRef([]);
  const raycasterRef        = useRef(new THREE.Raycaster());

  // Highlight
  const highlightStateRef = useRef({ mesh: null, originalEmissive: null, originalColor: null });

  // Medição (markers + linhas fixas + linha tracejada de prévia ao mover o mouse)
  const measurementObjectsRef = useRef({ markers: [], lines: [], previewLine: null });

  // Grade + eixos (helpers)
  const bimHelpersRef = useRef({ grid: null, axes: null });
  const [showGrid, setShowGrid] = useState(true);
  const [showAxes, setShowAxes] = useState(true);

  const [hoverTip, setHoverTip] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const viewerRootRef = useRef(null);
  const hoverFrameRef = useRef(null);
  const pendingHoverRef = useRef(null);

  // ── Limpeza do modelo IFC atual ─────────────────────────────────────────────
  const disposeCurrentIFC = useCallback(() => {
    const scene    = sceneRef.current;
    const loader   = ifcLoaderRef.current;
    const modelID  = ifcModelIdRef.current;

    disposeBimSceneHelpers(scene, bimHelpersRef);

    // Remove subsets da cena
    for (const layer of layerTogglesRef.current) {
      try { if (layer.subsetMesh && scene) scene.remove(layer.subsetMesh); } catch {}
    }

    // Remove modelo original e libera memória IFC
    if (modelRootRef.current && scene) {
      try { scene.remove(modelRootRef.current); } catch {}
    }
    if (loader && modelID !== null) {
      try { loader.ifcManager.removeIfcModel(modelID); } catch {}
    }

    ifcModelIdRef.current = null;
    modelRootRef.current  = null;
    selectableMeshesRef.current = [];
    setLayerToggles([]);
    setSelectedProps(null);
  }, []);

  // ── Limpeza das medições ────────────────────────────────────────────────────
  const removePreviewLine = useCallback(() => {
    const scene = sceneRef.current;
    const pl = measurementObjectsRef.current.previewLine;
    if (!scene || !pl) return;
    scene.remove(pl);
    pl.geometry?.dispose?.();
    pl.material?.dispose?.();
    measurementObjectsRef.current.previewLine = null;
  }, []);

  const clearMeasurements = useCallback(() => {
    const scene = sceneRef.current;
    const { markers, lines } = measurementObjectsRef.current;
    if (!scene) return;
    removePreviewLine();
    for (const m of markers) { scene.remove(m); m.geometry?.dispose?.(); m.material?.dispose?.(); }
    for (const l of lines)   { scene.remove(l); l.geometry?.dispose?.(); l.material?.dispose?.(); }
    measurementObjectsRef.current = { markers: [], lines: [], previewLine: null };
  }, [removePreviewLine]);

  const setOrUpdatePreviewLine = useCallback((p1, p2) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const prev = measurementObjectsRef.current.previewLine;
    if (prev) {
      scene.remove(prev);
      prev.geometry?.dispose?.();
      prev.material?.dispose?.();
    }
    const geom = new THREE.BufferGeometry().setFromPoints([p1, p2]);
    const mat  = new THREE.LineDashedMaterial({
      color: 0x38bdf8,
      dashSize: 0.25,
      gapSize: 0.15,
      transparent: true,
      opacity: 0.85,
    });
    const line = new THREE.Line(geom, mat);
    line.computeLineDistances();
    scene.add(line);
    measurementObjectsRef.current.previewLine = line;
  }, []);

  // ── Marcador de medição ─────────────────────────────────────────────────────
  const addMarker = useCallback(point => {
    const scene = sceneRef.current;
    if (!scene) return;
    const geo  = new THREE.SphereGeometry(0.18, 24, 24);
    const mat  = new THREE.MeshStandardMaterial({ color: 0x38bdf8, emissive: 0x082f49, emissiveIntensity: 0.75 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(point);
    scene.add(mesh);
    measurementObjectsRef.current.markers.push(mesh);
  }, []);

  const addOrUpdateDistanceLine = useCallback((p1, p2) => {
    const scene = sceneRef.current;
    if (!scene) return;
    for (const l of measurementObjectsRef.current.lines) scene.remove(l);
    measurementObjectsRef.current.lines = [];
    const geom = new THREE.BufferGeometry().setFromPoints([p1, p2]);
    const mat  = new THREE.LineBasicMaterial({ color: 0x38bdf8 });
    const line = new THREE.Line(geom, mat);
    scene.add(line);
    measurementObjectsRef.current.lines.push(line);
  }, []);

  const addOrUpdateAreaPolygon = useCallback(pts => {
    const scene = sceneRef.current;
    if (!scene || pts.length < 3) return;
    for (const l of measurementObjectsRef.current.lines) scene.remove(l);
    measurementObjectsRef.current.lines = [];
    const geom = new THREE.BufferGeometry().setFromPoints([...pts, pts[0]]);
    const mat  = new THREE.LineBasicMaterial({ color: 0x38bdf8 });
    const line = new THREE.Line(geom, mat);
    scene.add(line);
    measurementObjectsRef.current.lines.push(line);
  }, []);

  const computePolygonArea = useCallback(pts => {
    if (!Array.isArray(pts) || pts.length < 3) return null;
    let area = 0;
    for (let i = 1; i < pts.length - 1; i++) {
      const ab   = new THREE.Vector3().subVectors(pts[i],     pts[0]);
      const ac   = new THREE.Vector3().subVectors(pts[i + 1], pts[0]);
      const cross = new THREE.Vector3().crossVectors(ab, ac);
      area += cross.length() * 0.5;
    }
    return area;
  }, []);

  // ── Highlight ───────────────────────────────────────────────────────────────
  const highlightMesh = useCallback(mesh => {
    const hs = highlightStateRef.current;
    if (hs.mesh) {
      try {
        const mat = Array.isArray(hs.mesh.material) ? hs.mesh.material[0] : hs.mesh.material;
        if (hs.originalEmissive && mat?.emissive) mat.emissive.copy(hs.originalEmissive);
        if (hs.originalColor   && mat?.color)    mat.color.copy(hs.originalColor);
      } catch {}
    }
    highlightStateRef.current = { mesh: null, originalEmissive: null, originalColor: null };
    if (!mesh) return;
    try {
      const mat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      if (mat?.emissive) {
        highlightStateRef.current = { mesh, originalEmissive: mat.emissive.clone(), originalColor: null };
        mat.emissive.setHex(0xdc2626);
      } else if (mat?.color) {
        highlightStateRef.current = { mesh, originalEmissive: null, originalColor: mat.color.clone() };
        mat.color.setHex(0xdc2626);
      }
    } catch {}
  }, []);

  const clearHighlightAndSelection = useCallback(() => {
    highlightMesh(null);
    setSelectedProps(null);
  }, [highlightMesh]);

  // ── NDC do pointer ──────────────────────────────────────────────────────────
  const getPointerNDC = useCallback(e => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width)  * 2 - 1,
      -(((e.clientY - rect.top) / rect.height) * 2 - 1)
    );
  }, []);

  const computeSceneBoundingBox = useCallback(() => {
    const model = modelRootRef.current;
    const layers = layerTogglesRef.current;
    const box = new THREE.Box3();
    let has = false;
    for (const l of layers) {
      if (l.subsetMesh) {
        box.expandByObject(l.subsetMesh);
        has = true;
      }
    }
    if (!has && model) {
      box.setFromObject(model);
      has = true;
    }
    if (!has) return null;
    return box;
  }, []);

  const frameCameraFit = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.5;
    controls.target.copy(c2);
    cam.position.set(c2.x + dist, c2.y + dist * 0.6, c2.z + dist);
    cam.up.set(0, 1, 0);
    controls.update();
  }, [computeSceneBoundingBox]);

  const applyViewTop = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.8;
    controls.target.copy(c2);
    cam.up.set(0, 1, 0);
    cam.position.set(c2.x, c2.y + dist, c2.z);
    controls.update();
  }, [computeSceneBoundingBox]);

  const applyViewIso = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.5;
    const dir = new THREE.Vector3(1, 1, 1).normalize();
    controls.target.copy(c2);
    cam.up.set(0, 1, 0);
    cam.position.copy(c2).addScaledVector(dir, dist);
    controls.update();
  }, [computeSceneBoundingBox]);

  /** Câmera em +Z olhando para o centro (elevação frontal). */
  const applyViewFront = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.8;
    controls.target.copy(c2);
    cam.up.set(0, 1, 0);
    cam.position.set(c2.x, c2.y, c2.z + dist);
    controls.update();
  }, [computeSceneBoundingBox]);

  /** Vista lateral: câmera em +X. */
  const applyViewRight = useCallback(() => {
    const box = computeSceneBoundingBox();
    const cam = cameraRef.current;
    const controls = controlsRef.current;
    if (!box || box.isEmpty() || !cam || !controls) return;
    const c2 = box.getCenter(new THREE.Vector3());
    const s2 = box.getSize(new THREE.Vector3());
    const dist = Math.max(s2.x, s2.y, s2.z) * 1.8;
    controls.target.copy(c2);
    cam.up.set(0, 1, 0);
    cam.position.set(c2.x + dist, c2.y, c2.z);
    controls.update();
  }, [computeSceneBoundingBox]);

  const exportMeasurements = useCallback(() => {
    if (!measureMode || measurementPoints.length === 0) return;
    const payload = {
      exportedAt: new Date().toISOString(),
      measureMode,
      unit: measureMode === "distance" ? "m" : "m²",
      value: measurementValue,
      points: measurementPoints.map(p => [p.x, p.y, p.z]),
    };
    const blob = new Blob([safeJsonStringify(payload)], { type: "application/json;charset=utf-8" });
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = `bim-medicao-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [measureMode, measurementPoints, measurementValue]);

  const exportQuantitativos = useCallback(async () => {
    const loader  = ifcLoaderRef.current;
    const modelID = ifcModelIdRef.current;
    if (!loader || modelID === null || isExporting) return;

    setIsExporting(true);
    try {
      const rows           = [];
      const categorizedIds = new Set();

      const processCategory = async (catName, types) => {
        let ids = [];
        for (const type of types) {
          try {
            const typeIds = await loader.ifcManager.getAllItemsOfType(modelID, type, false);
            if (Array.isArray(typeIds)) ids = [...ids, ...typeIds];
          } catch {}
        }
        for (const id of ids) {
          if (categorizedIds.has(id)) continue;
          categorizedIds.add(id);
          try {
            const props = await loader.ifcManager.getItemProperties(modelID, id, false);
            const flat  = flattenIfcProps(props);
            rows.push({ Categoria: catName, ID: id, ...flat });
          } catch {}
        }
      };

      for (const cat of IFC_CATEGORIES) {
        await processCategory(cat.name, cat.types);
      }
      await processCategory("Outros", IFC_CATCHALL_TYPES);

      if (!rows.length) return;

      // Monta cabeçalhos: Categoria e ID primeiro, demais colunas em ordem de aparição
      const extraKeys = [];
      const keysSeen  = new Set();
      rows.forEach(r =>
        Object.keys(r).forEach(k => {
          if (k !== "Categoria" && k !== "ID" && !keysSeen.has(k)) {
            keysSeen.add(k);
            extraKeys.push(k);
          }
        })
      );
      const headers = ["Categoria", "ID", ...extraKeys];

      const escapeCell = v => {
        if (v === null || v === undefined) return "";
        const s = String(v);
        return s.includes(",") || s.includes('"') || s.includes("\n")
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      };

      const csv = [
        headers.join(","),
        ...rows.map(r => headers.map(h => escapeCell(r[h])).join(",")),
      ].join("\n");

      // BOM (\uFEFF) para o Excel reconhecer UTF-8 automaticamente
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const a    = document.createElement("a");
      const url  = URL.createObjectURL(blob);
      a.href     = url;
      a.download = `bim-quantitativos-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting]);

  const toggleFullscreen = useCallback(() => {
    const el = viewerRootRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const syncBimHelpers = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    disposeBimSceneHelpers(scene, bimHelpersRef);
    if (!showGrid && !showAxes) return;
    const box = computeSceneBoundingBox();
    if (!box || box.isEmpty()) return;
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const extent = Math.max(size.x, size.y, size.z);
    const h = bimHelpersRef.current;
    if (showGrid) {
      const gridSize = Math.max(size.x, size.z, 1) * 1.25;
      const div = Math.min(48, Math.max(12, Math.round(gridSize / 2)));
      const grid = new THREE.GridHelper(gridSize, div, 0x64748b, 0x334155);
      grid.position.set(center.x, box.min.y - 0.002, center.z);
      scene.add(grid);
      h.grid = grid;
    }
    if (showAxes) {
      const axLen = Math.max(0.5, extent * 0.22);
      const axes = new THREE.AxesHelper(axLen);
      axes.position.copy(center);
      scene.add(axes);
      h.axes = axes;
    }
  }, [showGrid, showAxes, computeSceneBoundingBox]);

  // ── Inicializa renderer/scene/camera (uma vez) ─────────────────────────────
  useEffect(() => {
    if (!canvasMountRef.current) return;

    const w = canvasMountRef.current.clientWidth  || window.innerWidth;
    const h = canvasMountRef.current.clientHeight || window.innerHeight;

    const scene    = new THREE.Scene();
    scene.background = new THREE.Color(0x0b1220);

    const camera   = new THREE.PerspectiveCamera(60, w / h, 0.01, 100000);
    camera.position.set(10, 10, 10);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);

    canvasMountRef.current.innerHTML = "";
    canvasMountRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    scene.add(new THREE.HemisphereLight(0xffffff, 0x223344, 0.7));
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(5, 10, 7);
    scene.add(dir);

    sceneRef.current    = scene;
    cameraRef.current   = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;

    const onResize = () => {
      if (!canvasMountRef.current) return;
      const nw = canvasMountRef.current.clientWidth  || window.innerWidth;
      const nh = canvasMountRef.current.clientHeight || window.innerHeight;
      renderer.setSize(nw, nh);
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    let animId = 0;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(animId);
      try { disposeBimSceneHelpers(scene, bimHelpersRef); } catch {}
      try { controls.dispose(); renderer.dispose(); } catch {}
      sceneRef.current = cameraRef.current = rendererRef.current = controlsRef.current = null;
      selectableMeshesRef.current = [];
      measurementObjectsRef.current = { markers: [], lines: [], previewLine: null };
    };
  }, []);

  useEffect(() => {
    const onFs = () => {
      setIsFullscreen(!!document.fullscreenElement);
      const mount = canvasMountRef.current;
      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      if (!mount || !renderer || !camera) return;
      const nw = mount.clientWidth || window.innerWidth;
      const nh = mount.clientHeight || window.innerHeight;
      renderer.setSize(nw, nh);
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // ── Click: seleção ou medição ───────────────────────────────────────────────
  useEffect(() => {
    const canvas = rendererRef.current?.domElement;
    if (!canvas) return;

    const onPointerDown = async e => {
      const scene  = sceneRef.current;
      const camera = cameraRef.current;
      if (!scene || !camera) return;

      const point = getPointerNDC(e);
      if (!point) return;

      raycasterRef.current.setFromCamera(point, camera);
      const hits = raycasterRef.current.intersectObjects(selectableMeshesRef.current, false);
      if (!hits.length) return;

      const hit      = hits[0];
      const hitPoint = hit.point.clone();

      // Modo medição (somente cliques — sem arraste; prévia = linha tracejada no pointermove)
      if (measureMode === "distance" || measureMode === "area") {
        if (measureMode === "distance" && measurementPoints.length >= 2) {
          clearMeasurements();
          setMeasurementPoints([hitPoint]);
          setMeasurementValue(null);
          addMarker(hitPoint);
          return;
        }
        const next = [...measurementPoints, hitPoint];
        setMeasurementPoints(next);
        setMeasurementValue(null);
        addMarker(hitPoint);
        removePreviewLine();
        if (measureMode === "distance" && next.length === 2) {
          addOrUpdateDistanceLine(next[0], next[1]);
          setMeasurementValue(next[0].distanceTo(next[1]));
        }
        if (measureMode === "area" && next.length >= 3) {
          addOrUpdateAreaPolygon(next);
          setMeasurementValue(computePolygonArea(next));
        }
        return;
      }

      // Modo seleção: obtém expressID via faceIndex e busca propriedades IFC
      const loader  = ifcLoaderRef.current;
      const modelID = ifcModelIdRef.current;
      if (!loader || modelID === null || hit.faceIndex === undefined) return;

      try {
        const expressID = loader.ifcManager.getExpressId(hit.object.geometry, hit.faceIndex);
        if (expressID === undefined || expressID === null) return;

        highlightMesh(hit.object);

        const props = await loader.ifcManager.getItemProperties(modelID, expressID, true);
        setSelectedProps(flattenIfcProps(props));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[BimViewer] erro ao obter propriedades:", err);
      }
    };

    const onPointerMove = e => {
      const cam = cameraRef.current;
      if (!cam) return;

      if (measureMode === "distance" || measureMode === "area") {
        setHoverTip(null);
        if (measureMode === "distance" && measurementPoints.length !== 1) {
          if (measurementPoints.length === 0) removePreviewLine();
          return;
        }
        if (measureMode === "area" && measurementPoints.length === 0) {
          removePreviewLine();
          return;
        }
        const ndc = getPointerNDC(e);
        if (!ndc) return;
        raycasterRef.current.setFromCamera(ndc, cam);
        const hits = raycasterRef.current.intersectObjects(selectableMeshesRef.current, false);
        if (!hits.length) {
          removePreviewLine();
          return;
        }
        const from = measurementPoints[measurementPoints.length - 1];
        setOrUpdatePreviewLine(from, hits[0].point);
        return;
      }

      removePreviewLine();
      const ndcHover = getPointerNDC(e);
      if (!ndcHover) {
        setHoverTip(null);
        return;
      }
      pendingHoverRef.current = { clientX: e.clientX, clientY: e.clientY, ndc: ndcHover };
      if (hoverFrameRef.current !== null) return;
      hoverFrameRef.current = requestAnimationFrame(() => {
        hoverFrameRef.current = null;
        const p = pendingHoverRef.current;
        if (!p) return;
        const cam2 = cameraRef.current;
        if (!cam2) return;
        raycasterRef.current.setFromCamera(p.ndc, cam2);
        const hits = raycasterRef.current.intersectObjects(selectableMeshesRef.current, false);
        if (!hits.length) {
          setHoverTip(null);
          return;
        }
        const hit = hits[0];
        const loader = ifcLoaderRef.current;
        const modelID = ifcModelIdRef.current;
        if (!loader || modelID === null || hit.faceIndex === undefined) {
          setHoverTip(null);
          return;
        }
        try {
          const expressID = loader.ifcManager.getExpressId(hit.object.geometry, hit.faceIndex);
          if (expressID === undefined || expressID === null) {
            setHoverTip(null);
            return;
          }
          let typeStr = "";
          try {
            const t = loader.ifcManager.getIfcType(modelID, expressID);
            if (t) typeStr = String(t);
          } catch { /* ignore */ }
          const text = typeStr ? `#${expressID} · ${typeStr}` : `ExpressID ${expressID}`;
          setHoverTip({ x: p.clientX, y: p.clientY, text });
        } catch {
          setHoverTip(null);
        }
      });
    };

    const onPointerLeave = () => {
      if (hoverFrameRef.current !== null) {
        cancelAnimationFrame(hoverFrameRef.current);
        hoverFrameRef.current = null;
      }
      pendingHoverRef.current = null;
      setHoverTip(null);
      removePreviewLine();
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerleave", onPointerLeave);
    return () => {
      if (hoverFrameRef.current !== null) {
        cancelAnimationFrame(hoverFrameRef.current);
        hoverFrameRef.current = null;
      }
      pendingHoverRef.current = null;
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [
    getPointerNDC, addMarker, addOrUpdateDistanceLine, addOrUpdateAreaPolygon,
    computePolygonArea, highlightMesh, measureMode, measurementPoints,
    clearMeasurements, removePreviewLine, setOrUpdatePreviewLine,
  ]);

  // ── Carrega modelo IFC ──────────────────────────────────────────────────────
  const loadActiveModel = useCallback(async () => {
    if (!folderPath || !token) return;

    setLoading(true);
    setLoadingMsg("Carregando…");
    setError(null);

    try {
      disposeCurrentIFC();
      clearMeasurements();
      setMeasurementPoints([]);
      setMeasurementValue(null);

      // Lista arquivos BIM do projeto
      const response = await api.post(
        "/projects/list_files",
        { folder_path: folderPath },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const list = response.data.files || [];
      // eslint-disable-next-line no-console
      console.log("[BIM] list_files resposta:", JSON.stringify(list));
      if (!list.length) {
        setError("Nenhum arquivo BIM encontrado neste projeto.");
        setAssets([]);
        setLoading(false);
        return;
      }

      setAssets(list);
      const chosen = list[0];
      setActiveIndex(0);

      // eslint-disable-next-line no-console
      console.log("[BIM] Asset escolhido:", JSON.stringify(chosen));
      const ifcUrl = chosen.ifcUrl || chosen.ifcPath;
      // eslint-disable-next-line no-console
      console.log("[BIM] ifcUrl resolvida:", ifcUrl);
      if (!ifcUrl) {
        setError("Arquivo IFC não encontrado. Faça upload novamente.");
        setLoading(false);
        return;
      }

      // Inicializa IFCLoader com o WASM.
      // Chamamos Init() diretamente com um locateFile customizado para garantir
      // URLs absolutas. O webpack bundle pode ter scriptDirectory errado
      // (derivado do bundle .js), causando 404 no wasm. Chamando Init() antes
      // de loadAsync(), o módulo já está carregado e loadAsync não re-inicializa.
      const loader = new IFCLoader();
      const publicUrl = process.env.PUBLIC_URL || "";
      const wasmBase = window.location.origin + publicUrl;
      // eslint-disable-next-line no-console
      console.log("[BIM] wasmBase:", wasmBase);

      // Determina qual WASM usar (MT requer crossOriginIsolated; ST é o fallback)
      const isCOI = typeof window !== "undefined" && window.crossOriginIsolated;
      const wasmFileName = isCOI ? "web-ifc-mt.wasm" : "web-ifc.wasm";
      const wasmFetchUrl = `${wasmBase}/${wasmFileName}`;
      // eslint-disable-next-line no-console
      console.log(`[BIM] crossOriginIsolated=${isCOI} → pré-carregando ${wasmFileName} de: ${wasmFetchUrl}`);

      // Pré-busca o WASM e injeta via wasmBinary nas opções do emscripten.
      // Isso bypassa completamente o fetch/streaming interno do emscripten
      // (que falha por MIME type em blob URLs e busca URL errada no fallback).
      let wasmBuf = null;
      try {
        const wasmResp = await fetch(wasmFetchUrl);
        wasmBuf = await wasmResp.arrayBuffer();
        const firstBytes = Array.from(new Uint8Array(wasmBuf, 0, 4))
          .map(b => b.toString(16).padStart(2, "0")).join(" ");
        // eslint-disable-next-line no-console
        console.warn(`[BIM] WASM fetch status=${wasmResp.status} content-type=${wasmResp.headers.get("content-type")} bytes[0..3]=${firstBytes} (correto: 00 61 73 6d)`);

        // Valida magic bytes — se for HTML, o servidor retornou o fallback SPA
        if (firstBytes !== "00 61 73 6d") {
          const preview = new TextDecoder().decode(new Uint8Array(wasmBuf, 0, 60));
          // eslint-disable-next-line no-console
          console.error("[BIM] WASM inválido! Conteúdo recebido:", preview);
          throw new Error(
            `WASM inválido (status=${wasmResp.status}, bytes=${firstBytes}).\n` +
            `URL: ${wasmFetchUrl}\n` +
            `Início do conteúdo: "${preview.slice(0, 80)}"`
          );
        }
        // eslint-disable-next-line no-console
        console.warn("[BIM] WASM pré-carregado com sucesso, byteLength:", wasmBuf.byteLength);
      } catch (fetchErr) {
        // eslint-disable-next-line no-console
        console.error("[BIM] Falha ao pré-buscar WASM:", fetchErr);
        throw fetchErr; // Repassa para o catch principal exibir na UI
      }

      // eslint-disable-next-line no-console
      console.log("[BIM] Chamando Init() com wasmBinary pré-carregado...");
      // Passa o buffer WASM diretamente via extraModuleOptions.
      // Isso evita completamente o fetch interno do emscripten (que falhava por MIME type
      // no streaming e depois buscava URL errada no fallback).
      await loader.ifcManager.state.api.Init(
        (path) => {
          // locateFile ainda necessário para worker.js (MT mode)
          if (path.endsWith(".worker.js")) return `${wasmBase}/${path}`;
          return path;
        },
        { wasmBinary: wasmBuf }
      );
      // eslint-disable-next-line no-console
      console.log("[BIM] Init() concluído com sucesso.");
      await loader.ifcManager.applyWebIfcConfig({
        COORDINATE_TO_ORIGIN: true,
        USE_FAST_BOOLEANS: false,
      });
      ifcLoaderRef.current = loader;

      const scene = sceneRef.current;
      if (!scene) return;

      // Carrega modelo IFC
      setLoadingMsg("Lendo arquivo IFC…");
      // eslint-disable-next-line no-console
      console.log("[BIM] Iniciando loadAsync:", ifcUrl);
      const model   = await loader.loadAsync(ifcUrl);
      // eslint-disable-next-line no-console
      console.log("[BIM] loadAsync concluído. modelID:", model?.modelID);
      const modelID = model.modelID;
      ifcModelIdRef.current = modelID;
      modelRootRef.current  = model;

      // Adiciona à cena (invisível inicialmente — subsets serão a renderização)
      model.visible = false;
      scene.add(model);

      // Centraliza e normaliza escala
      const box    = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      const size   = box.getSize(new THREE.Vector3());
      model.position.sub(center);
      const maxDim = Math.max(size.x, size.y, size.z);
      if (maxDim > 0) {
        const target = 20;
        const scale  = target / maxDim;
        if (scale < 1) model.scale.multiplyScalar(scale);
      }

      // Constrói subsets por categoria IFC
      setLoadingMsg("Construindo camadas…");
      const builtLayers   = [];
      const subsetMeshes  = [];
      const categorizedIds = new Set();

      const buildSubset = (name, ids) => {
        // Cada camada precisa de um material com UUID distinto: o SubsetManager
        // indexa por matID = material.uuid + modelID. Com o material padrão, todas
        // as camadas compartilham a mesma chave e removePrevious:true apaga a anterior.
        const subset = loader.ifcManager.createSubset({
          modelID,
          ids,
          scene,
          removePrevious: true,
          material: new THREE.MeshLambertMaterial({
            color: 0xc5d0e0,
            side: THREE.DoubleSide,
          }),
        });
        if (subset) {
          subset.position.copy(model.position);
          subset.scale.copy(model.scale);
          builtLayers.push({ name, visible: true, subsetMesh: subset });
          subsetMeshes.push(subset);
        }
      };

      for (const cat of IFC_CATEGORIES) {
        let ids = [];
        for (const type of cat.types) {
          try {
            const typeIds = await loader.ifcManager.getAllItemsOfType(modelID, type, false);
            if (Array.isArray(typeIds)) ids = [...ids, ...typeIds];
          } catch {}
        }
        if (!ids.length) continue;
        ids.forEach(id => categorizedIds.add(id));
        buildSubset(cat.name, ids);
      }

      // Catch-all: captura elementos de tipos conhecidos que não foram categorizados
      {
        const othersIds = [];
        for (const type of IFC_CATCHALL_TYPES) {
          try {
            const typeIds = await loader.ifcManager.getAllItemsOfType(modelID, type, false);
            if (Array.isArray(typeIds)) {
              typeIds.forEach(id => { if (!categorizedIds.has(id)) othersIds.push(id); });
            }
          } catch {}
        }
        if (othersIds.length) buildSubset("Outros", othersIds);
      }

      if (builtLayers.length > 0) {
        selectableMeshesRef.current = subsetMeshes;
        layerTogglesRef.current = builtLayers;
        setLayerToggles(builtLayers);
      } else {
        layerTogglesRef.current = [];
        // Fallback: mostra modelo original se nenhuma categoria encontrada
        model.visible = true;
        const meshes = [];
        model.traverse(o => { if (o.isMesh) meshes.push(o); });
        selectableMeshesRef.current = meshes;
      }

      // Câmera + helpers (grade/eixos) — usa o mesmo encaixe que o atalho R
      frameCameraFit();
      syncBimHelpers();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[BimViewer] erro ao carregar IFC:", err);
      // eslint-disable-next-line no-console
      console.error("[BimViewer] stack:", err?.stack);
      setError("Falha ao carregar o modelo BIM.");
    } finally {
      setLoading(false);
      setLoadingMsg("Carregando…");
    }
  }, [folderPath, token, disposeCurrentIFC, clearMeasurements, frameCameraFit, syncBimHelpers]);

  useEffect(() => {
    if (folderPath && token) loadActiveModel();
  }, [folderPath, token, loadActiveModel]);

  // Atualiza grade/eixos ao alternar visibilidade ou ao mudar camadas
  useEffect(() => {
    if (loading || error) return;
    if (!modelRootRef.current) return;
    syncBimHelpers();
  }, [showGrid, showAxes, layerToggles, loading, error, syncBimHelpers]);

  // Atalhos: Esc · R · 1–3
  useEffect(() => {
    const onKeyDown = e => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;

      if (e.key === "Escape") {
        e.preventDefault();
        setMeasureMode(null);
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);
        setHoverTip(null);
        clearHighlightAndSelection();
        return;
      }
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        frameCameraFit();
        return;
      }
      if (e.key === "1") {
        e.preventDefault();
        setMeasureMode(null);
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);
        return;
      }
      if (e.key === "2") {
        e.preventDefault();
        setMeasureMode("distance");
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);
        return;
      }
      if (e.key === "3") {
        e.preventDefault();
        setMeasureMode("area");
        clearMeasurements();
        setMeasurementPoints([]);
        setMeasurementValue(null);
        return;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    clearMeasurements, clearHighlightAndSelection, frameCameraFit,
  ]);

  // ── Toggle de camada ────────────────────────────────────────────────────────
  const onToggleLayer = useCallback(layerName => {
    setLayerToggles(prev =>
      prev.map(l => {
        if (l.name !== layerName) return l;
        if (l.subsetMesh) l.subsetMesh.visible = !l.visible;
        return { ...l, visible: !l.visible };
      })
    );
  }, []);

  // ── Labels de medição ────────────────────────────────────────────────────────
  const measurementLabel = useMemo(() => {
    if (measurementValue === null || measurementValue === undefined) return null;
    if (measureMode === "distance") return `Distância: ${measurementValue.toFixed(2)} m`;
    if (measureMode === "area")     return `Área: ${measurementValue.toFixed(2)} m²`;
    return null;
  }, [measurementValue, measureMode]);

  const measureHelpText = useMemo(() => {
    if (measureMode === "distance") {
      return "Dois cliques na superfície do modelo (não use arraste): primeiro extremo, depois o segundo. A linha tracejada amarela é só prévia até o próximo clique. Após ver o valor, clique de novo para medir outro trecho.";
    }
    if (measureMode === "area") {
      return "Vários cliques na superfície: cada clique é um vértice do polígono (mínimo 3). A tracejada mostra a prévia até o próximo clique. Gire a cena com o botão direito/drag nos controles de órbita.";
    }
    return "Escolha Distância ou Área nos botões abaixo. Atalhos: Esc limpar · R encaixar · 1 Selecionar · 2 Distância · 3 Área.";
  }, [measureMode]);

  const activeAsset = assets[activeIndex];

  const handleCloseBim = () => {
    const projectName = project?.name;
    navigate("/projects", {
      state: projectName ? { openProject: projectName } : undefined,
    });
  };

  return (
    <div className="bim-viewer-mount bim-viewer-mount--dashboard">
    <div className="bim-viewer-root" ref={viewerRootRef}>
      <div className="QuitButtonTour">
        <button type="button" title="Fechar" aria-label="Fechar e voltar aos projetos" onClick={handleCloseBim}>
          <CloseIcon style={{ fontSize: 20 }} />
        </button>
      </div>

      {/* Header */}
      <div className="bim-viewer__header">
        <div className="bim-viewer__title">
          BIM Viewer
          {loading  && <span className="bim-viewer__status">{loadingMsg}</span>}
          {!loading && error && <span className="bim-viewer__status bim-viewer__status--error">Erro</span>}
        </div>

        <div className="bim-viewer__spacer" />

        {assets.length > 1 && (
          <select
            className="bim-viewer__select"
            value={activeIndex}
            onChange={e => setActiveIndex(Number(e.target.value))}
            aria-label="Selecionar modelo"
          >
            {assets.map((a, i) => (
              <option key={a.name || i} value={i}>{a.name || `Modelo ${i + 1}`}</option>
            ))}
          </select>
        )}
      </div>

      {/* Canvas */}
      <div className="bim-viewer__canvas-wrap" ref={canvasMountRef} />

      {hoverTip && (
        <div
          className="bim-viewer__hover-tooltip"
          style={{ left: hoverTip.x + 14, top: hoverTip.y + 14 }}
        >
          {hoverTip.text}
        </div>
      )}

      {/* Painel esquerdo: camadas */}
      {layersPanelOpen ? (
        <div className="bim-viewer__panel bim-viewer__panel--left">
          <div className="bim-viewer__panel-title">
            <LayersIcon fontSize="small" /> Camadas
            <button
              type="button"
              className="bim-viewer__panel-collapse-btn"
              onClick={() => setLayersPanelOpen(false)}
              aria-label="Colapsar camadas"
              title="Colapsar"
            >
              <ExpandLessIcon fontSize="small" />
            </button>
          </div>
          {layerToggles.length === 0 ? (
            <div className="bim-viewer__panel-empty">
              {loading ? "Construindo…" : "Sem camadas"}
            </div>
          ) : (
            <div className="bim-viewer__layers">
              {layerToggles.map(l => (
                <label key={l.name} className="bim-viewer__layer-item">
                  <input
                    type="checkbox"
                    checked={l.visible}
                    onChange={() => onToggleLayer(l.name)}
                  />
                  <span className="bim-viewer__layer-name">{l.name}</span>
                </label>
              ))}
            </div>
          )}
          {layerToggles.length > 0 && (
            <>
              <div className="bim-viewer__divider" />
              <button
                type="button"
                className="bim-viewer__export-btn"
                onClick={exportQuantitativos}
                disabled={isExporting}
                title="Exportar todos os elementos como planilha CSV"
              >
                <GetAppIcon fontSize="small" />
                {isExporting ? "Exportando…" : "Quantitativos (CSV)"}
              </button>
            </>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="bim-viewer__panel-collapsed-btn bim-viewer__panel-collapsed-btn--left"
          onClick={() => setLayersPanelOpen(true)}
          aria-label="Mostrar camadas"
          title="Mostrar camadas"
        >
          <LayersIcon fontSize="small" />
          Camadas
          <ExpandMoreIcon fontSize="small" />
        </button>
      )}

      {/* Painel direito: propriedades + medição */}
      {propsPanelOpen ? (
        <div className="bim-viewer__panel bim-viewer__panel--right">
          <div className="bim-viewer__panel-title">
            <PlaceIcon fontSize="small" /> Propriedades
            <button
              type="button"
              className="bim-viewer__panel-collapse-btn"
              onClick={() => setPropsPanelOpen(false)}
              aria-label="Colapsar propriedades"
              title="Colapsar"
            >
              <ExpandLessIcon fontSize="small" />
            </button>
          </div>
          {selectedProps ? (
            <div className="bim-viewer__meta">
              <pre className="bim-viewer__meta-pre">
                {safeJsonStringify(selectedProps)}
              </pre>
            </div>
          ) : (
            <div className="bim-viewer__panel-empty">
              Clique em um elemento para ver as propriedades IFC.
            </div>
          )}

          <div className="bim-viewer__divider" />

          <div className="bim-viewer__panel-title">
            <StraightenIcon2 fontSize="small" /> Medição
          </div>
          <div className="bim-viewer__measure-info">
            <div className="bim-viewer__measure-mode">{measureHelpText}</div>
            {measurementLabel && (
              <div className="bim-viewer__measure-value">{measurementLabel}</div>
            )}
            {(measureMode === "distance" || measureMode === "area") &&
              measurementPoints.length > 0 && (
                <div className="bim-viewer__measure-export">
                  <button
                    type="button"
                    className="bim-viewer__export-btn"
                    onClick={exportMeasurements}
                  >
                    <GetAppIcon />
                    Exportar medições (JSON)
                  </button>
                </div>
              )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="bim-viewer__panel-collapsed-btn bim-viewer__panel-collapsed-btn--right"
          onClick={() => setPropsPanelOpen(true)}
          aria-label="Mostrar propriedades"
          title="Mostrar propriedades"
        >
          <PlaceIcon fontSize="small" />
          Propriedades
          <ExpandMoreIcon fontSize="small" />
        </button>
      )}

      {/* Barra inferior: esquerda (ferramentas) · centro (vistas) · direita (captura/ocorrências) */}
      <div className="bim-viewer__controls">
        <div className="bim-viewer__controls-slot bim-viewer__controls-slot--left">
        <div className="bim-viewer__controls-group bim-viewer__controls-group--left">
          <Tooltip title="Selecionar elemento (propriedades IFC) · atalho 1">
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--compact ${measureMode === null ? "active" : ""}`}
              onClick={() => {
                setMeasureMode(null);
                clearMeasurements();
                setMeasurementPoints([]);
                setMeasurementValue(null);
              }}
            >
              Selecionar
            </button>
          </Tooltip>
          <Tooltip title="Medir distância · atalho 2">
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--icon-only ${measureMode === "distance" ? "active" : ""}`}
              onClick={() => {
                setMeasureMode("distance");
                clearMeasurements();
                setMeasurementPoints([]);
                setMeasurementValue(null);
              }}
              aria-label="Medir distância"
            >
              <StraightenIcon fontSize="small" />
            </button>
          </Tooltip>
          <Tooltip title="Medir área (polígono) · atalho 3">
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--icon-only ${measureMode === "area" ? "active" : ""}`}
              onClick={() => {
                setMeasureMode("area");
                clearMeasurements();
                setMeasurementPoints([]);
                setMeasurementValue(null);
              }}
              aria-label="Medir área"
            >
              <SquareFootIcon fontSize="small" />
            </button>
          </Tooltip>
          <Tooltip title="Limpar medições">
            <button
              type="button"
              className="bim-viewer__tool-btn bim-viewer__tool-btn--icon-only"
              onClick={() => { clearMeasurements(); setMeasurementPoints([]); setMeasurementValue(null); }}
              aria-label="Limpar medições"
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Sair da tela cheia" : "Tela cheia"}>
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--icon-only ${isFullscreen ? "active" : ""}`}
              onClick={toggleFullscreen}
              aria-label="Tela cheia"
            >
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </button>
          </Tooltip>
        </div>
        </div>

        <div className="bim-viewer__controls-slot bim-viewer__controls-slot--center">
        <div className="bim-viewer__controls-group bim-viewer__controls-group--center">
          <Tooltip title="Vista de cima (planta)">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={applyViewTop}>
              Topo
            </button>
          </Tooltip>
          <Tooltip title="Vista isométrica">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={applyViewIso}>
              Iso
            </button>
          </Tooltip>
          <Tooltip title="Vista frontal (elevação)">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={applyViewFront}>
              Frontal
            </button>
          </Tooltip>
          <Tooltip title="Vista lateral direita">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={applyViewRight}>
              Direita
            </button>
          </Tooltip>
          <Tooltip title="Encaixar modelo na tela (atalho R)">
            <button type="button" className="bim-viewer__tool-btn bim-viewer__tool-btn--compact" onClick={frameCameraFit}>
              Encaixar
            </button>
          </Tooltip>
          <Tooltip title={showGrid ? "Ocultar grade" : "Mostrar grade"}>
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--compact ${showGrid ? "active" : ""}`}
              onClick={() => setShowGrid(v => !v)}
            >
              Grade
            </button>
          </Tooltip>
          <Tooltip title={showAxes ? "Ocultar eixos XYZ" : "Mostrar eixos XYZ"}>
            <button
              type="button"
              className={`bim-viewer__tool-btn bim-viewer__tool-btn--compact ${showAxes ? "active" : ""}`}
              onClick={() => setShowAxes(v => !v)}
            >
              Eixos
            </button>
          </Tooltip>
          <span className="bim-viewer__shortcuts-hint" aria-hidden="true">
            Esc · R · 1–3
          </span>
        </div>
        </div>

        <div className="bim-viewer__controls-slot bim-viewer__controls-slot--right">
          {folderPath && !error && (
            <div className="bim-viewer__controls-group bim-viewer__controls-group--right">
              <ScreenshotBim rendererRef={rendererRef} folderPath={folderPath} variant="bim" />
            </div>
          )}
        </div>
      </div>

      {/* Erro */}
      {error && !loading && (
        <div className="bim-viewer__error">
          <p>{error}</p>
          <button type="button" className="bim-viewer__back-btn" onClick={() => navigate("/projects")}>
            Voltar
          </button>
        </div>
      )}
    </div>
    </div>
  );
};

const mapStateToProps = state => ({ token: state.auth.accessToken });
export default connect(mapStateToProps)(BimViewer);
